import { useCallback } from 'react';

import { AnalyticsStep } from '@atlaskit/adf-schema/steps';
import { FabricChannel } from '@atlaskit/analytics-listeners/types';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { ACTION_SUBJECT_ID } from '@atlaskit/editor-common/analytics';
import {
	ACTION,
	ACTION_SUBJECT,
	EVENT_TYPE,
	INPUT_METHOD,
	MODE,
} from '@atlaskit/editor-common/analytics';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { EditorState, Transaction } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import type { AIPlugin } from '../../../aiPluginType';
import type { EditorAIAnalyticEventPayload } from '../../../types/analytics';
import { getPackageInfo } from '../version';

import {
	type FailedStep,
	type ServerErrorStep,
	type UserFlowStepAttributes,
} from './analyticsFlowTypes';

export function addAIPluginCommonInfoToPayload(payload: EditorAIAnalyticEventPayload) {
	const packageInfo = getPackageInfo();
	const attributes = payload.attributes || {};

	return {
		...payload,
		attributes: {
			...attributes,
			aiPluginPackageName: packageInfo.packageName,
			aiPluginPackageVersion: packageInfo.packageVersion,
		},
	};
}

// This utils is referenced from packages/editor/editor-core/src/analytics-api/attach-payload-into-transaction.ts
// Note: this adds extra step to a transaction which effectively marks it as docChanged and can cause editor onchange to fire on dispatch
function attachPayloadIntoTransaction({
	editorState,
	payload,
	channel = FabricChannel.editor,
}: {
	channel?: string;
	editorState: EditorState;
	payload: EditorAIAnalyticEventPayload;
}) {
	return (tr: Transaction) => {
		const { storedMarks } = tr;
		const pos = tr.mapping.map(editorState.selection.$from.pos, -1);
		tr.step(
			new AnalyticsStep(
				[
					{
						payload: addAIPluginCommonInfoToPayload(payload),
						channel,
					},
				],
				[],
				// We don't understand now why we need position in AnalyticsStep.
				// So we are keeping same as it is in original util.
				// Below comment is taken from original util from packages/editor/editor-core/src/analytics-api/attach-payload-into-transaction.ts
				// We need to create the step based on a position, this prevent split history for relative changes.
				pos,
			),
		);

		// We don't understand now why we need to store marks again in transaction.
		// So we are keeping same as it is in original util.
		// Below comment is taken from original util from packages/editor/editor-core/src/analytics-api/attach-payload-into-transaction.ts
		// When you add a new step all the storedMarks are removed it
		if (storedMarks) {
			tr.setStoredMarks(storedMarks);
		}

		return true;
	};
}

/**
 *  This will add extra step to the transaction with analytics info
 *  this information will be be picked up by the analytics plugin which will fire the event
 *  please note that this will cause the transaction to be marked as docChanged because of adding extra transaction step
 *  avoid creating and dispatching adhoc transaction, but rather reuse existing transactions that are already dispatched
 *
 * The `addToHistory` parameter controls whether this transaction should be recorded in the
 * editor's history. By default, `addToHistory` is set to `true`, meaning the transaction
 * will be added to the history stack, allowing for undo/redo operations. However, if
 * `addToHistory` is set to `false`, the transaction will not be recorded in the history,
 * which can be useful for operations that should not be undoable, such as logging or
 * non-destructive analytics events.
 */
export function addAnalytics({
	editorState,
	tr,
	payload,
	channel = FabricChannel.editor,
	addToHistory = true,
}: {
	addToHistory?: boolean;
	channel?: string;
	editorState: EditorState;
	payload: EditorAIAnalyticEventPayload;
	tr: Transaction;
}): Transaction {
	attachPayloadIntoTransaction({ editorState, payload, channel })(tr);
	if (!addToHistory) {
		tr.setMeta('addToHistory', false);
	}
	return tr;
}

// File copied from packages/editor/editor-core/src/types/command.ts
export type AnalyticsEventPayloadCallback = (
	state: EditorState,
) => EditorAIAnalyticEventPayload | undefined;
export type CommandDispatch = (tr: Transaction) => void;
export type Command = (
	state: EditorState,
	dispatch?: CommandDispatch,
	view?: EditorView,
) => boolean;
export type HigherOrderCommand = (command: Command) => Command;

// Below function is copied from packages/editor/editor-core/src/plugins/analytics/utils.ts
export function withAnalytics({
	payload,
	channel = FabricChannel.editor,
}: {
	channel?: string;
	payload: EditorAIAnalyticEventPayload | AnalyticsEventPayloadCallback;
}): HigherOrderCommand {
	return (command) => (state, dispatch, view) =>
		command(
			state,
			(tr) => {
				if (dispatch) {
					if (payload instanceof Function) {
						const dynamicPayload = payload(state);
						if (dynamicPayload) {
							dispatch(
								addAnalytics({
									editorState: state,
									tr,
									payload: dynamicPayload,
									channel,
								}),
							);
						}
					} else {
						dispatch(addAnalytics({ editorState: state, tr, payload, channel }));
					}
				}
			},
			view,
		);
}
export type FireAIAnalyticsEvent = {
	channel?: string;
	payload: EditorAIAnalyticEventPayload;
};

export type FireAIAnalyticsEventCallback = (args: FireAIAnalyticsEvent) => void;

export function createFireAIAnalyticsEvent(
	createAnalyticsEvent: CreateUIAnalyticsEvent,
): FireAIAnalyticsEventCallback {
	return ({ payload, channel = FabricChannel.editor }: FireAIAnalyticsEvent) => {
		if (!createAnalyticsEvent) {
			return;
		}

		const fireEvent = () =>
			createAnalyticsEvent(addAIPluginCommonInfoToPayload(payload))?.fire(channel);
		// Ignored via go/ees005
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		if ((window as any).requestIdleCallback) {
			// Ignored via go/ees005
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(window as any).requestIdleCallback(fireEvent);
			// Ignored via go/ees005
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} else if ((window as any).requestAnimationFrame) {
			// Ignored via go/ees005
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(window as any).requestAnimationFrame(fireEvent);
		} else {
			setTimeout(fireEvent, 0);
		}
	};
}

/*
 * gets helper function to fire analytics for ai plugin
 */
export function useFireAIAnalyticsEvent() {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const fireAIAnalyticsEvent: FireAIAnalyticsEventCallback = useCallback(
		(args) => createFireAIAnalyticsEvent(createAnalyticsEvent)(args),
		[createAnalyticsEvent],
	);
	return fireAIAnalyticsEvent;
}

/**
 * Gets the single instrumentation ID from the given channel ID and last AI interaction ID.
 */
export function getSingleInstrumentationID(channelId?: string, lastAiInteractionID?: string) {
	return channelId ?? lastAiInteractionID ?? '';
}

/**
 * Checks if the given step is an error step.
 */
export function isErrorStepAttributes(
	step: Partial<UserFlowStepAttributes> | undefined,
): step is Partial<ServerErrorStep['attributes'] | FailedStep['attributes']> {
	if (!step) {
		return false;
	}

	return 'errorKey' in step || 'errorType' in step || 'statusCode' in step;
}

/**
 * Emits a UI "button viewed" event when an AI button becomes visible
 * in the editor’s floating toolbar.
 */
export const fireButtonViewedAnalyticsEvent = ({
	api,
	actionSubjectId,
	attributes,
}: {
	actionSubjectId:
		| ACTION_SUBJECT_ID.AI_ASK_ROVO_BUTTON
		| ACTION_SUBJECT_ID.AI_HERO_PROMPT_BUTTON
		| ACTION_SUBJECT_ID.AI_DEFINITIONS_DEFINE_BUTTON;
	api?: ExtractInjectionAPI<AIPlugin>;
	attributes: { selectionCharCount: number; selectionWordCount: number };
}): void => {
	api?.analytics?.actions.fireAnalyticsEvent({
		action: ACTION.VIEWED,
		actionSubject: ACTION_SUBJECT.BUTTON,
		actionSubjectId,
		eventType: EVENT_TYPE.UI,
		attributes: {
			inputMethod: INPUT_METHOD.FLOATING_TB,
			mode: MODE.EDITOR,
			selectionCharCount: attributes.selectionCharCount,
			selectionWordCount: attributes.selectionWordCount,
		},
	});
};

function countSegments(input: string, granularity: 'grapheme' | 'word'): number {
	const seg = new Intl.Segmenter('und', { granularity });
	let n = 0;
	for (const _ of seg.segment(input)) {
		n++;
	}
	return n;
}

function countWordLike(input: string): number {
	const seg = new Intl.Segmenter('und', { granularity: 'word' });
	let n = 0;
	for (const part of seg.segment(input)) {
		if (part.isWordLike) {
			n++;
		}
	}
	return n;
}

const computeCharWordCounts = (
	raw: string,
): {
	charCount: number;
	wordCount: number;
} => {
	// drop all whitespace for char count
	// @ts-ignore - TS1501 TypeScript 5.9.2 upgrade
	const noWhitespace = raw.replace(/\s+/gu, '');

	// normalize whitespace for word count
	// @ts-ignore - TS1501 TypeScript 5.9.2 upgrade
	const normalized = raw.replace(/\s+/gu, ' ').trim();

	const hasSegmenter = typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function';

	const charCount = hasSegmenter
		? countSegments(noWhitespace, 'grapheme')
		: Array.from(noWhitespace).length;

	const wordCount =
		normalized === ''
			? 0
			: hasSegmenter
				? countWordLike(normalized)
				: // simple language-agnostic fallback
					// @ts-ignore - TS1501 TypeScript 5.9.2 upgrade
					normalized.split(/\s+/u).length;

	return { charCount, wordCount };
};

export const rangeCharWordCount = (
	doc: PMNode,
	from: number,
	to: number,
): { charCount: number; wordCount: number } => {
	if (from === to) {
		return { charCount: 0, wordCount: 0 };
	}
	// keep line breaks as newlines and leaf nodes as spaces
	const raw = doc.textBetween(from, to, '\n', ' ');
	return computeCharWordCounts(raw);
};

export const selectionCharWordCount = (
	state: EditorState,
): {
	charCount: number;
	wordCount: number;
} => {
	const { from, to } = state.selection;
	if (state.selection.empty) {
		return { charCount: 0, wordCount: 0 };
	}
	const raw = state.doc.textBetween(from, to, '\n', ' ');
	return computeCharWordCounts(raw);
};
